import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { Message as MessageType } from '../../hooks/useChat';

interface MessageProps {
  message: MessageType;
  isStreaming?: boolean;
}

const Message: React.FC<MessageProps> = ({ message, isStreaming }) => {
  const isUser = message.role === 'user';
  const isError = message.isError;

  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  };

  if (isError) {
    return (
      <div className="flex justify-center mb-4 animate-fade-in">
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2 max-w-[90%]">
          <p className="text-sm text-red-400 font-mono">{message.content}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4 animate-fade-in`}>
      <div className={`flex ${isUser ? 'flex-row-reverse' : 'flex-row'} items-start gap-3 max-w-[85%]`}>
        {/* Avatar */}
        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          isUser 
            ? 'bg-dev-accent/20 border border-dev-accent/50' 
            : 'bg-dev-accent/10 border border-dev-accent/30'
        }`}>
          <span className={`text-xs font-mono font-bold ${
            isUser ? 'text-dev-accent' : 'text-dev-accent'
          }`}>
            {isUser ? 'U' : 'T'}
          </span>
        </div>

        {/* Message Bubble */}
        <div className="flex flex-col gap-1">
          {!isUser && (
            <span className="text-xs font-mono font-semibold text-dev-accent px-1">TARS</span>
          )}
          <div className={`rounded-2xl px-4 py-3 ${
            isUser 
              ? 'bg-dev-accent/15 border border-dev-accent/30 rounded-tr-sm' 
              : 'bg-dev-bg-card border border-dev-border rounded-tl-sm'
          }`}>
            {isUser ? (
              <p className="text-sm leading-relaxed whitespace-pre-wrap break-words text-dev-text">
                {message.content}
              </p>
            ) : (
              <div className={`text-sm leading-relaxed text-dev-text-muted prose prose-invert prose-sm max-w-none
                prose-p:my-2 prose-p:leading-relaxed
                prose-ul:my-2 prose-ul:list-disc prose-ul:pl-4
                prose-ol:my-2 prose-ol:list-decimal prose-ol:pl-4
                prose-li:my-0.5
                prose-strong:text-dev-accent prose-strong:font-semibold
                prose-em:text-dev-text-muted prose-em:italic
                prose-code:text-dev-accent-cyan prose-code:bg-dev-bg-light prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:font-mono
                prose-pre:bg-dev-bg-light prose-pre:border prose-pre:border-dev-border prose-pre:rounded-lg prose-pre:p-3 prose-pre:my-2
                prose-a:text-dev-accent prose-a:underline hover:prose-a:text-dev-accent-bright
                prose-h1:text-base prose-h1:font-bold prose-h1:text-dev-text prose-h1:mt-3 prose-h1:mb-2
                prose-h2:text-sm prose-h2:font-bold prose-h2:text-dev-text prose-h2:mt-3 prose-h2:mb-2
                prose-h3:text-sm prose-h3:font-semibold prose-h3:text-dev-text prose-h3:mt-2 prose-h3:mb-1
                prose-blockquote:border-l-2 prose-blockquote:border-dev-accent prose-blockquote:pl-3 prose-blockquote:italic prose-blockquote:text-dev-text-dimmed
              `}>
                <ReactMarkdown
                  skipHtml
                  disallowedElements={['script', 'style', 'iframe', 'object', 'embed']}
                >
                  {message.content}
                </ReactMarkdown>
                {isStreaming && (
                  <span className="inline-block w-1.5 h-3.5 bg-dev-accent align-text-bottom ml-0.5 animate-cursor-blink" />
                )}
              </div>
            )}
          </div>
          <span className={`text-xs text-dev-text-dimmed px-1 ${isUser ? 'text-right' : 'text-left'}`}>
            {formatTime(message.timestamp)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default Message;
